import {
  runTransaction,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";
import { isAndroid, isNative, isIOS, notifications } from "@/lib/platform";
import { AltenotPermissions } from "@/plugins/altenotPermissions";

export type OperationalPermissionId = "notifications" | "location" | "camera";

interface OperationalPermissionsState {
  completed: boolean;
  completedAt: string;
}

const STORAGE_KEY = "altenot_operational_permissions_v1";

export const OPERATIONAL_PERMISSIONS: ReadonlyArray<OperationalPermissionId> = [
  "notifications",
  "location",
  "camera",
];

export function requiresOperationalPermissions(): boolean {
  return isNative;
}

export function hasOperationalPermissions(): boolean {
  return (
    txRead<OperationalPermissionsState | null>(STORAGE_KEY, null)?.completed ===
    true
  );
}

export function markOperationalPermissionsCompleted(): void {
  runTransaction(() => {
    txWrite(STORAGE_KEY, {
      completed: true,
      completedAt: new Date().toISOString(),
    } satisfies OperationalPermissionsState);
  });
}

async function requestBrowserCameraPermission(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}

async function requestBrowserLocationPermission(): Promise<boolean> {
  if (!("geolocation" in navigator)) return false;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

export async function requestOperationalPermission(
  permission: OperationalPermissionId,
): Promise<boolean> {
  if (permission === "notifications") {
    return notifications.requestPermission();
  }

  if (permission === "location") {
    if (isAndroid) return (await AltenotPermissions.requestLocation()).granted;
    return requestBrowserLocationPermission();
  }

  if (isAndroid) return (await AltenotPermissions.requestCamera()).granted;
  return requestBrowserCameraPermission();
}

export async function openOperationalPermissionsSettings(): Promise<boolean> {
  try {
    if (isAndroid) {
      await AltenotPermissions.openApplicationSettings();
      return true;
    }
    if (isIOS) {
      window.location.assign("app-settings:");
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

export function getOperationalPermissionLabel(
  permission: OperationalPermissionId,
): string {
  if (permission === "notifications") return "Notificaciones operativas";
  if (permission === "location") return "Ubicación";
  return "Cámara";
}
